/**
 * Computed entity for environment variable configuration.
 *
 * Reads from EnvVarRegistry, crosses with process.env,
 * masks sensitive values, and exposes as read-only table.
 * Only ADMIN can access.
 */

import type { ComputedEntityDefinition, ModuleContext } from '@gzl10/nexus-sdk'
import { useTextField, useSelectField, useCheckboxField, useLocalizedField } from '@gzl10/nexus-sdk/fields'
import type { EnvVarRegistry } from './env-config.registry.js'

/**
 * Masks a value if it is marked as sensitive.
 * - URLs: masks the password component (user:*** @ host)
 * - Plain strings: replaced entirely with ***
 */
function maskValue(value: string, sensitive: boolean): string {
  if (!sensitive) return value
  try {
    const url = new URL(value)
    if (url.password) {
      url.password = '***'
      return url.toString()
    }
  } catch { /* not a URL, mask entirely */ }
  return '***'
}

export const envConfigEntity: ComputedEntityDefinition = {
  type: 'computed',
  label: { en: 'Environment', es: 'Entorno' },
  icon: 'mdi:cog-outline',
  labelField: 'name',
  routePrefix: '/env-config',
  defaultSort: { field: 'category', order: 'asc' },

  fields: {
    name: useTextField({
      label: { en: 'Variable', es: 'Variable' },
      size: 100,
      nullable: false,
      meta: { sortable: true, searchable: true }
    }),
    category: {
      ...useSelectField({
        label: { en: 'Category', es: 'Categoría' },
        options: [
          { value: 'server', label: { en: 'Server', es: 'Servidor' } },
          { value: 'auth', label: { en: 'Authentication', es: 'Autenticación' } },
          { value: 'mail', label: { en: 'Mail', es: 'Correo' } },
          { value: 'logger', label: { en: 'Logger', es: 'Logger' } },
          { value: 'observability', label: { en: 'Observability', es: 'Observabilidad' } },
          { value: 'storage', label: { en: 'Storage', es: 'Almacenamiento' } },
          { value: 'advanced', label: { en: 'Advanced', es: 'Avanzado' } }
        ],
        nullable: false,
        meta: { sortable: true }
      })
    },
    source: useTextField({
      label: { en: 'Source', es: 'Origen' },
      size: 50,
      nullable: false,
      meta: { sortable: true }
    }),
    value: useTextField({
      label: { en: 'Value', es: 'Valor' },
      size: 255,
      nullable: true
    }),
    default: useTextField({
      label: { en: 'Default', es: 'Por defecto' },
      size: 100,
      nullable: true
    }),
    configured: useCheckboxField({
      label: { en: 'Set', es: 'Configurada' },
      meta: { sortable: true }
    }),
    required: useCheckboxField({
      label: { en: 'Required', es: 'Requerida' },
      meta: { sortable: true }
    }),
    sensitive: useCheckboxField({
      label: { en: 'Sensitive', es: 'Sensible' }
    }),
    description: useLocalizedField({
      label: { en: 'Description', es: 'Descripción' },
      required: false
    })
  },

  casl: {
    subject: 'SystemEnvConfig',
    permissions: {
      ADMIN: { actions: ['read'] }
    }
  },

  compute: async (ctx: ModuleContext) => {
    const registry = ctx.services.get<EnvVarRegistry>('envVarRegistry')
    if (!registry) return []

    return registry.getAll().map(entry => {
      const raw = process.env[entry.name]
      const configured = raw !== undefined
      return {
        name: entry.name,
        category: entry.category,
        source: entry.source,
        value: configured ? maskValue(raw, entry.sensitive ?? false) : null,
        default: entry.default ?? null,
        configured,
        required: entry.required,
        sensitive: entry.sensitive ?? false,
        description: entry.description
      }
    })
  },

  cache: {
    ttl: 300 // 5 minutes — env vars don't change at runtime
  }
}
